const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const APP_MEMBERSHIP_SUBSCRIPTION_NOS: readonly number[] = [21, 22, 23];
const WEB_MEMBERSHIP_SUBSCRIPTION_NOS: readonly number[] = [24, 25];

type MembershipSubscriptionChannel = 'APP' | 'WEB' | 'UNKNOWN';

const SUBSCRIPTION_NO_KEYS = [
  'SubscriptionNo',
  'subscriptionNo',
  'SubNo',
  'subNo',
  'Sub_No',
  'sub_no',
  'SubscriptionCode',
  'subscriptionCode',
  'CurrentSubscriptionNo',
  'currentSubscriptionNo',
];

export const hasStoredCplatToken = () => {
  return Boolean(getStoredCplatToken());
};

export const getStoredCplatToken = () => {
  if (typeof localStorage === 'undefined') return '';

  return normalizeCplatToken(localStorage.getItem('cplatToken'));
};

export const resolveMobileMembershipCplatToken = (
  reduxToken?: string | null,
  queryStringToken?: string | null,
) => {
  return (
    normalizeCplatToken(reduxToken) || normalizeCplatToken(queryStringToken) || getStoredCplatToken()
  );
};

export const normalizeCplatToken = (token?: string | null) => {
  const trimmedToken = token?.trim();
  if (!trimmedToken) return '';

  try {
    const parsedToken: unknown = JSON.parse(trimmedToken);
    if (typeof parsedToken === 'string') return parsedToken.trim();
  } catch {
    // Non-JSON legacy values are normalized below.
  }

  return trimmedToken.replace(/^['"]|['"]$/g, '').trim();
};

export const getIsAnySubscribed = (serviceInfo: unknown) => {
  if (!isRecord(serviceInfo)) return false;

  const appSellerInfo = serviceInfo.AppSellerInfo;
  if (!isRecord(appSellerInfo)) return false;

  return appSellerInfo.IsAnySubscribed === true;
};

export const getMembershipSubscriptionNo = (serviceInfo: unknown) => {
  if (!isRecord(serviceInfo)) return null;

  const appSellerInfo = serviceInfo.AppSellerInfo;
  if (!isRecord(appSellerInfo)) return null;

  const subscriptionNo = readSubscriptionNo(appSellerInfo);
  if (subscriptionNo !== null) return subscriptionNo;

  const nestedRecord = Object.values(appSellerInfo).find(isRecord);
  return nestedRecord ? readSubscriptionNo(nestedRecord) : null;
};

export const getMembershipSubscriptionChannel = (
  serviceInfo: unknown,
): MembershipSubscriptionChannel => {
  const subscriptionNo = getMembershipSubscriptionNo(serviceInfo);
  if (subscriptionNo === null) return 'UNKNOWN';
  if (APP_MEMBERSHIP_SUBSCRIPTION_NOS.includes(subscriptionNo)) return 'APP';
  if (WEB_MEMBERSHIP_SUBSCRIPTION_NOS.includes(subscriptionNo)) return 'WEB';

  return 'UNKNOWN';
};

export const getIsAppMembershipSubscribed = (serviceInfo: unknown) => {
  return getIsAnySubscribed(serviceInfo) && getMembershipSubscriptionChannel(serviceInfo) === 'APP';
};

export const getIsWebMembershipSubscribed = (serviceInfo: unknown) => {
  return getIsAnySubscribed(serviceInfo) && getMembershipSubscriptionChannel(serviceInfo) === 'WEB';
};

const readNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return null;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const readSubscriptionNo = (record: Record<string, unknown>) => {
  return (
    SUBSCRIPTION_NO_KEYS.map((key) => readNumber(record[key])).find(
      (value): value is number => value !== null,
    ) ?? null
  );
};
